import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PaperPlaneTilt, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { RECIPIENT_STATUS, CAMPAIGN_AUDIENCES } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TONE = {
  no_response: "bg-slate-100 text-slate-700 border-slate-200",
  opened: "bg-blue-100 text-blue-800 border-blue-200",
  replied: "bg-amber-100 text-amber-800 border-amber-200",
  converted: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function ResponseTracking() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/coll/campaigns").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (campaign, recipientId, status) => {
    const recipients = (campaign.recipients || []).map((r) => (r.id === recipientId ? { ...r, status } : r));
    try { await api.put(`/coll/campaigns/${campaign.id}`, { recipients }); toast.success("Response updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading response tracking…</div>;

  const withRecipients = items.filter((c) => (c.recipients || []).length > 0);
  const all = withRecipients.flatMap((c) => c.recipients || []);
  const count = (k) => all.filter((r) => (r.status || "no_response") === k).length;

  return (
    <div className="space-y-6" data-testid="response-tracking-page">
      <PageHeader overline="AI Outreach" title="Response Tracking" subtitle="Per-recipient status so follow-through is easy to see" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="No response" value={count("no_response")} testid="stat-noresp" />
        <Stat label="Opened" value={count("opened")} tone="text-blue-600" testid="stat-opened" />
        <Stat label="Replied" value={count("replied")} tone="text-amber-600" testid="stat-replied" />
        <Stat label="Converted to job" value={count("converted")} tone="text-emerald-600" testid="stat-conv" />
      </div>

      {withRecipients.length === 0 ? (
        <EmptyState icon={Megaphone}>No campaign recipients yet. <Link to="/app/outreach/new" className="text-accent hover:underline">Create a campaign</Link> to start tracking.</EmptyState>
      ) : (
        <div className="space-y-4">
          {withRecipients.map((c) => (
            <Card key={c.id} className="border-border overflow-hidden" data-testid={`track-campaign-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 bg-primary text-white">
                <div className="flex items-center gap-2 min-w-0">
                  <PaperPlaneTilt size={17} weight="fill" className="text-accent shrink-0" />
                  <span className="font-display font-semibold truncate">{c.name}</span>
                </div>
                <span className="text-[11px] text-white/60">{CAMPAIGN_AUDIENCES[c.audience] || c.audience} · {(c.recipients || []).length} recipients</span>
              </div>
              <div className="divide-y divide-border">
                {(c.recipients || []).map((r) => (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`recipient-row-${r.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-primary truncate">{r.label}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.contact}{r.phone ? ` · ${r.phone}` : ""}{r.email ? ` · ${r.email}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${TONE[r.status || "no_response"]}`}>{RECIPIENT_STATUS[r.status || "no_response"]}</span>
                      <Select value={r.status || "no_response"} onValueChange={(v) => setStatus(c, r.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-[160px]" data-testid={`recipient-status-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(RECIPIENT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
